import type { SupabaseClient } from "@supabase/supabase-js";

export type ConsumableItemType = "feed" | "medicine" | "supply" | "other";
export type InventoryMovementType = "purchase" | "usage" | "adjustment" | "waste";
export type AssetStatus = "active" | "maintenance" | "inactive" | "retired";

export interface ConsumableInventoryItem {
  id: string;
  itemCode: string;
  itemType: ConsumableItemType;
  displayName: string;
  feedProductId?: string;
  baseUnit: string;
  reorderLevel: number;
  isActive: boolean;
  domainVersion: number;
  onHand: number;
  lowStock: boolean;
}

export interface ConsumableInventoryMovement {
  id: string;
  itemId: string;
  movementType: InventoryMovementType;
  quantityDelta: number;
  unitCostToman?: number;
  financeTransactionId?: string;
  feedingEventId?: string;
  occurredOn: string;
  notes?: string;
  createdAt: string;
}

export interface AssetRegisterItem {
  id: string;
  assetCode: string;
  assetName: string;
  category: string;
  quantity: number;
  locationText?: string;
  status: AssetStatus;
  purchaseValueToman?: number;
  note?: string;
  domainVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssetRegisterEvent {
  id: string;
  assetId: string;
  eventType: "created" | "updated" | "status_changed";
  statusBefore?: AssetStatus;
  statusAfter?: AssetStatus;
  amountToman?: number;
  financeTransactionId?: string;
  occurredOn: string;
  note?: string;
  createdAt: string;
}

export interface WarehouseAssetSnapshot {
  items: ConsumableInventoryItem[];
  movements: ConsumableInventoryMovement[];
  assets: AssetRegisterItem[];
  assetEvents: AssetRegisterEvent[];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function numberValue(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return 0;
}

function itemType(value: unknown): ConsumableItemType {
  if (value === "feed" || value === "medicine" || value === "supply" || value === "other") {
    return value;
  }
  return "other";
}

function movementType(value: unknown): InventoryMovementType {
  if (value === "purchase" || value === "usage" || value === "adjustment" || value === "waste") {
    return value;
  }
  return "adjustment";
}

function assetStatus(value: unknown): AssetStatus {
  if (
    value === "active" ||
    value === "maintenance" ||
    value === "inactive" ||
    value === "retired"
  ) {
    return value;
  }
  return "inactive";
}

export function consumableItemTypeLabel(value: ConsumableItemType): string {
  if (value === "feed") return "غذا";
  if (value === "medicine") return "دارو";
  if (value === "supply") return "ملزومات";
  return "سایر";
}

export function inventoryMovementTypeLabel(value: InventoryMovementType): string {
  if (value === "purchase") return "ورود / خرید";
  if (value === "usage") return "مصرف";
  if (value === "waste") return "ضایعات";
  return "اصلاح موجودی";
}

export function assetStatusLabel(value: AssetStatus): string {
  if (value === "active") return "فعال";
  if (value === "maintenance") return "تعمیر / سرویس";
  if (value === "retired") return "از رده خارج";
  return "غیرفعال";
}

export async function loadWarehouseAssetSnapshot(
  client: SupabaseClient,
  farmId: string,
): Promise<WarehouseAssetSnapshot> {
  const [inventoryResult, assetResult] = await Promise.all([
    client.rpc("consumable_inventory_snapshot_v1", { target_farm_id: farmId }),
    client.rpc("asset_register_snapshot_v1", { target_farm_id: farmId }),
  ]);

  if (inventoryResult.error) throw inventoryResult.error;
  if (assetResult.error) throw assetResult.error;

  const inventoryRoot = record(inventoryResult.data);
  const assetRoot = record(assetResult.data);

  const items = rows(inventoryRoot.items).map((row): ConsumableInventoryItem => {
    const feedProductId = optionalString(row.feedProductId);
    return {
      id: String(row.id),
      itemCode: String(row.itemCode ?? ""),
      itemType: itemType(row.itemType),
      displayName: String(row.displayName ?? ""),
      ...(feedProductId ? { feedProductId } : {}),
      baseUnit: String(row.baseUnit ?? ""),
      reorderLevel: numberValue(row.reorderLevel),
      isActive: row.isActive === true,
      domainVersion: numberValue(row.domainVersion),
      onHand: numberValue(row.onHand),
      lowStock: row.lowStock === true,
    };
  });

  const movements = rows(inventoryRoot.movements).map((row): ConsumableInventoryMovement => {
    const financeTransactionId = optionalString(row.financeTransactionId);
    const feedingEventId = optionalString(row.feedingEventId);
    const notes = optionalString(row.notes);
    return {
      id: String(row.id),
      itemId: String(row.itemId),
      movementType: movementType(row.movementType),
      quantityDelta: numberValue(row.quantityDelta),
      ...(row.unitCostToman === null || row.unitCostToman === undefined
        ? {}
        : { unitCostToman: numberValue(row.unitCostToman) }),
      ...(financeTransactionId ? { financeTransactionId } : {}),
      ...(feedingEventId ? { feedingEventId } : {}),
      occurredOn: String(row.occurredOn ?? ""),
      ...(notes ? { notes } : {}),
      createdAt: String(row.createdAt ?? ""),
    };
  });

  const assets = rows(assetRoot.assets).map((row): AssetRegisterItem => {
    const locationText = optionalString(row.locationText);
    const note = optionalString(row.note);
    return {
      id: String(row.id),
      assetCode: String(row.assetCode ?? ""),
      assetName: String(row.assetName ?? ""),
      category: String(row.category ?? "equipment"),
      quantity: numberValue(row.quantity),
      ...(locationText ? { locationText } : {}),
      status: assetStatus(row.status),
      ...(row.purchaseValueToman === null || row.purchaseValueToman === undefined
        ? {}
        : { purchaseValueToman: numberValue(row.purchaseValueToman) }),
      ...(note ? { note } : {}),
      domainVersion: numberValue(row.domainVersion),
      createdAt: String(row.createdAt ?? ""),
      updatedAt: String(row.updatedAt ?? ""),
    };
  });

  const assetEvents = rows(assetRoot.events).map((row): AssetRegisterEvent => {
    const statusBefore = optionalString(row.statusBefore);
    const statusAfter = optionalString(row.statusAfter);
    const financeTransactionId = optionalString(row.financeTransactionId);
    const note = optionalString(row.note);
    return {
      id: String(row.id),
      assetId: String(row.assetId),
      eventType:
        row.eventType === "created" || row.eventType === "status_changed"
          ? row.eventType
          : "updated",
      ...(statusBefore ? { statusBefore: assetStatus(statusBefore) } : {}),
      ...(statusAfter ? { statusAfter: assetStatus(statusAfter) } : {}),
      ...(row.amountToman === null || row.amountToman === undefined
        ? {}
        : { amountToman: numberValue(row.amountToman) }),
      ...(financeTransactionId ? { financeTransactionId } : {}),
      occurredOn: String(row.occurredOn ?? ""),
      ...(note ? { note } : {}),
      createdAt: String(row.createdAt ?? ""),
    };
  });

  return { items, movements, assets, assetEvents };
}
